import { useEffect, useState } from 'react';

interface HeroSectionProps {
  onQuoteClick: () => void;
}

const rotatingWords = ['On Time.', 'On Budget.', 'Worldwide.', 'Certified.'];

const stats = [
  { val: '10+', label: 'Years Experience' },
  { val: '50+', label: 'Countries Served' },
  { val: '200+', label: 'Mill Partners' },
  { val: '500K+', label: 'MT Shipped/Year' },
  { val: '24/7', label: 'WhatsApp Support' },
];

export default function HeroSection({ onQuoteClick }: HeroSectionProps) {
  const [wordIndex, setWordIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setWordIndex((i) => (i + 1) % rotatingWords.length);
        setVisible(true);
      }, 400);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section className="relative w-full min-h-[100svh] md:h-[700px] overflow-hidden flex items-center">
      {/* Background */}
      <img
        src="https://storage.readdy-site.link/project_files/0c67b98a-5efe-4a8e-a1fa-e6ce09f7829e/267b87ee-0f0a-4a98-a84a-8071ff0930b5_asian-metal.jpeg?v=43f3c3f33e1dc10b2622f421d4523572"
        alt="Steel Link Global - Quality Steel Shipped Worldwide"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/65 to-black/85 md:bg-gradient-to-r md:from-black/90 md:via-black/60 md:to-black/20" />

      {/* Content */}
      <div className="relative z-10 w-full pt-28 pb-20 md:pt-0 md:pb-0">
        <div className="max-w-[1420px] mx-auto px-6 w-full">
          <div className="max-w-2xl mx-auto md:mx-0 text-center md:text-left">

            {/* Badge */}
            <div className="inline-flex items-center gap-2 text-white text-xs font-black px-4 py-2 rounded-full mb-5 tracking-wide" style={{ backgroundColor: '#0a2342' }}>
              <i className="ri-global-line" />
              Trusted by Buyers in 50+ Countries
            </div>

            {/* Headline — scaled for mobile */}
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-2">
              Steel You Can Trust,
            </h1>
            <div className="text-3xl sm:text-5xl lg:text-6xl font-black leading-tight mb-5 h-[1.3em] flex items-center justify-center md:justify-start">
              <span
                style={{
                  color: '#7eb8f7',
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(10px)',
                  transition: 'opacity 0.35s ease, transform 0.35s ease',
                  display: 'inline-block',
                }}
              >
                {rotatingWords[wordIndex]}
              </span>
            </div>

            <p className="text-sm md:text-lg text-white/80 mb-7 leading-relaxed max-w-xl mx-auto md:mx-0">
              We ship steel from China to your port. Fast quotes, fair prices, and real support — every order, every time.
            </p>

            {/* Trust pills */}
            <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-7">
              {[
                { icon: 'ri-checkbox-circle-fill', text: 'Mill Certified' },
                { icon: 'ri-ship-line', text: 'FOB Shipping' },
                { icon: 'ri-bank-card-line', text: 'L/C & TT' },
                { icon: 'ri-shield-check-line', text: 'SGS Inspected' },
              ].map((b) => (
                <span key={b.text} className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                  <i className={b.icon} style={{ color: '#7eb8f7' }} />
                  {b.text}
                </span>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
              <button
                onClick={onQuoteClick}
                className="w-full sm:w-auto px-8 py-4 text-white font-black rounded-lg active:scale-95 transition-all cursor-pointer text-sm text-center"
                style={{ backgroundColor: '#0a2342' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#0d2d56'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#0a2342'; }}
              >
                <i className="ri-file-list-3-line mr-2" />
                Get a Free Quote
              </button>
              <a
                href="https://wa.me/8617671262710"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-black rounded-lg active:scale-95 transition-all cursor-pointer text-sm text-center flex items-center justify-center gap-2"
              >
                <i className="ri-whatsapp-line text-base" />
                WhatsApp Us
              </a>
            </div>

            {/* Response time */}
            <p className="mt-4 text-white/50 text-xs text-center md:text-left">
              <i className="ri-time-line mr-1" style={{ color: '#7eb8f7' }} />
              We reply within 2 hours · Mon–Fri 9AM–6PM CST
            </p>
          </div>
        </div>
      </div>

      {/* Mobile stats bar — shown on mobile only */}
      <div className="absolute bottom-0 left-0 right-0 md:hidden border-t border-white/10" style={{ backgroundColor: 'rgba(10,35,66,0.85)' }}>
        <div className="flex overflow-x-auto scrollbar-hide px-4 py-3 gap-6">
          {stats.map((s) => (
            <div key={s.label} className="text-center flex-shrink-0">
              <div className="font-black text-base whitespace-nowrap" style={{ color: '#7eb8f7' }}>{s.val}</div>
              <div className="text-white/60 text-xs whitespace-nowrap">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Desktop stats bar */}
      <div className="absolute bottom-0 left-0 right-0 backdrop-blur-sm border-t border-white/10 hidden md:block" style={{ backgroundColor: 'rgba(10,35,66,0.75)' }}>
        <div className="max-w-[1420px] mx-auto px-6 py-3 flex items-center justify-between gap-6">
          {stats.map((s) => (
            <div key={s.label} className="text-center flex-1">
              <div className="font-black text-lg" style={{ color: '#7eb8f7' }}>{s.val}</div>
              <div className="text-white/60 text-xs">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator — desktop only, fades out on scroll */}
      <div
        className="absolute bottom-20 right-10 hidden md:flex flex-col items-center gap-1.5 transition-opacity duration-500"
        style={{ opacity: scrolled ? 0 : 0.5 }}
      >
        <span className="text-white text-xs tracking-widest uppercase" style={{ writingMode: 'vertical-rl' }}>Scroll</span>
        <div className="w-px h-10 bg-white/40 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full bg-white animate-bounce" style={{ height: '40%' }} />
        </div>
      </div>
    </section>
  );
}
